import React, { useState, useRef, useEffect } from "react";
import { FileCheck, LogOut, Trash2, Camera, KeyRound, Check, AlertTriangle, Monitor, Sun, Moon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { request } from "../service/HttpService";
import { getPhotoPresigned, saveUserPhotoKey, deleteUserPhoto } from "../service/UserService";
import { fetchUserSettings, saveUserSettings } from "../service/UserSettingsService";
import { deleteAccountCall } from "../service/DeleteAccountService";
import { logoutCall } from "../service/AuthService";
import { useTheme } from "./ThemeContext";
import {ftAccessToken, ftRefreshToken, ftUserPhoto, ftUnreadCount} from "../constants/Constants";
import CoralReef from "../images/CoralReef.jpg";
import Frankophage from "../images/Frankophage.jpg";

const tabs = ["Account", "Preferences", "Privacy"];

const accountActions = [
    { label: "Change Password", icon: KeyRound, danger: false, path: "/change-password" },
    { label: "Verify Report", icon: FileCheck, danger: false, path: "/report/verify" },
    { label: "Log Out", icon: LogOut, danger: false, path: "/login" },
    { label: "Delete Account", icon: Trash2, danger: true, path: null },
];

const themeOptions = [
    { value: "system", label: "System", icon: Monitor },
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
];

// Theme is a device-local preference (see ThemeContext), so it applies instantly
// and sits outside the server-backed autosave below.
function Appearance() {
    const { preference, setPreference } = useTheme();
    return (
        <div className="settings-card">
            <p className="settings-card-title">Appearance</p>
            <p className="settings-card-sub">System follows your device's light or dark setting. Saved on this device.</p>
            <div className="pref-view-options pref-theme-options" role="radiogroup" aria-label="Theme">
                {themeOptions.map(({ value, label, icon: Icon }) => (
                    <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={preference === value}
                        className={`pref-view-btn${preference === value ? " pref-view-active" : ""}`}
                        onClick={() => setPreference(value)}
                    >
                        <Icon size={15} />
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );
}

const DEFAULT_PREFS = {
    notifications: true,
    storeManuscripts: false,
    showScoreHistory: true,
    peerReviewNotifications: true,
    openToReview: false,
    featuredEligible: false,
    featuredShowAuthor: false,
    featuredAllowDownload: false,
    feedOptIn: false,
    profilePublic: false,
};

// Featured sub-options are only meaningful while "eligible" is on, so they're
// only persisted as true in that case (mirrors the onboarding/backend rules).
const toPayload = (p) => ({
    ...p,
    featuredShowAuthor: p.featuredEligible && p.featuredShowAuthor,
    featuredAllowDownload: p.featuredEligible && p.featuredAllowDownload,
});

function Preferences() {
    const [prefs, setPrefs] = useState(DEFAULT_PREFS);
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
    // prefsRef mirrors prefs synchronously, so back-to-back toggles always build
    // the payload from the latest state — React batching can't make it stale.
    const prefsRef = useRef(DEFAULT_PREFS);
    const pendingRef = useRef(null);   // newest payload waiting to be sent (or null)
    const inFlightRef = useRef(false); // a save request is currently in flight
    const debounceRef = useRef(null);
    const savedHideRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        fetchUserSettings(true)
            .then(res => {
                const s = res.settings ?? {};
                const next = { ...DEFAULT_PREFS };
                for (const key of Object.keys(DEFAULT_PREFS)) {
                    if (s[key] != null) next[key] = s[key];
                }
                prefsRef.current = next;
                setPrefs(next);
            })
            .catch(() => {})
            .finally(() => { if (mountedRef.current) setLoading(false); });
    }, []);

    // On unmount: stop touching state, clear timers, and send any change still
    // queued (debounced but not yet sent) so a toggle right before navigating
    // away isn't lost. An in-flight save re-flushes the queue itself on finish.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(debounceRef.current);
            clearTimeout(savedHideRef.current);
            if (pendingRef.current != null && !inFlightRef.current) {
                saveUserSettings(pendingRef.current, true).catch(() => {});
                pendingRef.current = null;
            }
        };
    }, []);

    // Send the newest queued payload, one request at a time. Serializing saves
    // (never two in flight) means the server can't be overwritten by an older,
    // slower request and a stale response can't clobber the status indicator.
    const flush = () => {
        if (inFlightRef.current || pendingRef.current == null) return;
        const body = pendingRef.current;
        pendingRef.current = null;
        inFlightRef.current = true;
        saveUserSettings(body, true)
            .then(() => {
                inFlightRef.current = false;
                if (pendingRef.current != null) {
                    flush(); // a newer change arrived mid-flight — send it next
                } else if (mountedRef.current) {
                    setSaveStatus("saved");
                    clearTimeout(savedHideRef.current);
                    savedHideRef.current = setTimeout(() => {
                        if (mountedRef.current) setSaveStatus("idle");
                    }, 2000);
                }
            })
            .catch(() => {
                inFlightRef.current = false;
                if (pendingRef.current == null) pendingRef.current = body; // keep for retry
                if (mountedRef.current) setSaveStatus("error");
            });
    };

    // Persist the full (normalized) settings after a short debounce so rapid
    // toggles collapse into a single request that always carries the latest state.
    const queueSave = (payload) => {
        pendingRef.current = payload;
        setSaveStatus("saving");
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(flush, 500);
    };

    const update = (patch) => {
        const next = { ...prefsRef.current, ...patch };
        prefsRef.current = next;
        setPrefs(next);
        queueSave(toPayload(next));
    };

    const retry = () => {
        setSaveStatus("saving");
        flush();
    };

    if (loading) return (
        <>
            <div className="settings-card">
                <p className="settings-card-title">Notifications & Storage</p>
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="pref-toggle-row">
                        <div style={{ flex: 1 }}>
                            <div className="skeleton" style={{ height: 12, width: "40%", marginBottom: 8 }} />
                            <div className="skeleton" style={{ height: 10, width: "75%" }} />
                        </div>
                        <div className="skeleton" style={{ height: 24, width: 44, borderRadius: 12 }} />
                    </div>
                ))}
            </div>
            <div className="settings-card">
                <div className="skeleton" style={{ height: 12, width: 120, marginBottom: 14 }} />
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="pref-toggle-row">
                        <div style={{ flex: 1 }}>
                            <div className="skeleton" style={{ height: 12, width: "40%", marginBottom: 8 }} />
                            <div className="skeleton" style={{ height: 10, width: "75%" }} />
                        </div>
                        <div className="skeleton" style={{ height: 24, width: 44, borderRadius: 12 }} />
                    </div>
                ))}
            </div>
        </>
    );

    return (
        <>
            <div className="settings-card">
                <p className="settings-card-title">Notifications & Storage</p>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Allow Notifications</p>
                        <p className="pref-toggle-desc">Receive alerts when analyses complete or require review.</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.notifications ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ notifications: !prefs.notifications })}
                        aria-pressed={prefs.notifications}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Store My Manuscripts</p>
                        <p className="pref-toggle-desc">Allows you to download your manuscripts at a later date and submit them for peer review. Required for peer review eligibility.</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.storeManuscripts ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ storeManuscripts: !prefs.storeManuscripts })}
                        aria-pressed={prefs.storeManuscripts}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
            </div>

            <div className="settings-card">
                <p className="settings-card-title">Dashboard</p>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Show Score History</p>
                        <p className="pref-toggle-desc">Display the score history chart on your dashboard.</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.showScoreHistory ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ showScoreHistory: !prefs.showScoreHistory })}
                        aria-pressed={prefs.showScoreHistory}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
            </div>

            <div className="settings-card">
                <p className="settings-card-title">Peer Review</p>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Peer Review Notifications</p>
                        <p className="pref-toggle-desc">Get notified when your peer review status changes (assigned, in review, completed).</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.peerReviewNotifications ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ peerReviewNotifications: !prefs.peerReviewNotifications })}
                        aria-pressed={prefs.peerReviewNotifications}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
            </div>

            <div className="settings-card">
                <p className="settings-card-title">Paper of the Week</p>
                <p className="settings-card-sub">Highest-scoring submissions can be highlighted on every researcher's dashboard for one week. Opt in below to be considered.</p>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Eligible to be featured</p>
                        <p className="pref-toggle-desc">If your paper has the top overall score this week, it may be displayed on the community dashboard.</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.featuredEligible ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ featuredEligible: !prefs.featuredEligible })}
                        aria-pressed={prefs.featuredEligible}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
                <div className={`featured-suboptions${prefs.featuredEligible ? "" : " featured-suboptions-disabled"}`}>
                    <div className="pref-toggle-row pref-toggle-sub">
                        <div>
                            <p className="pref-toggle-label">Show my name</p>
                            <p className="pref-toggle-desc">Display your name and profile photo on the featured card. Off shows "Anonymous Researcher."</p>
                        </div>
                        <button
                            className={`pref-toggle${prefs.featuredEligible && prefs.featuredShowAuthor ? " pref-toggle-on" : ""}`}
                            onClick={() => prefs.featuredEligible && update({ featuredShowAuthor: !prefs.featuredShowAuthor })}
                            aria-pressed={prefs.featuredEligible && prefs.featuredShowAuthor}
                            disabled={!prefs.featuredEligible}
                        >
                            <span className="pref-toggle-thumb" />
                        </button>
                    </div>
                    <div className="pref-toggle-row pref-toggle-sub">
                        <div>
                            <p className="pref-toggle-label">Allow viewers to download the PDF</p>
                            <p className="pref-toggle-desc">Make your manuscript file downloadable from the featured card. Requires "Store My Manuscripts."</p>
                        </div>
                        <button
                            className={`pref-toggle${prefs.featuredEligible && prefs.featuredAllowDownload ? " pref-toggle-on" : ""}`}
                            onClick={() => prefs.featuredEligible && update({ featuredAllowDownload: !prefs.featuredAllowDownload })}
                            aria-pressed={prefs.featuredEligible && prefs.featuredAllowDownload}
                            disabled={!prefs.featuredEligible}
                        >
                            <span className="pref-toggle-thumb" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="settings-card">
                <p className="settings-card-title">Community Feed</p>
                <p className="settings-card-sub">Share your wins with the DeepThought community. When you opt in, milestones from your work — high scores, deep thinker matches, personal bests — appear on every researcher's feed.</p>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Include me in the community feed</p>
                        <p className="pref-toggle-desc">Future achievements will be shared with your name and photo. Past events stay hidden until they're created. You can opt out anytime.</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.feedOptIn ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ feedOptIn: !prefs.feedOptIn })}
                        aria-pressed={prefs.feedOptIn}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
            </div>

            <div className="settings-card">
                <p className="settings-card-title">Public Profile</p>
                <p className="settings-card-sub">Your profile shows your stats, score history, submissions, and recent wins. When public, anyone with the link can view it; otherwise the page shows a private notice.</p>
                <div className="pref-toggle-row">
                    <div>
                        <p className="pref-toggle-label">Make my profile public</p>
                        <p className="pref-toggle-desc">Visitors can see your scores and activity but cannot export, share, or delete anything. Off keeps your profile private.</p>
                    </div>
                    <button
                        className={`pref-toggle${prefs.profilePublic ? " pref-toggle-on" : ""}`}
                        onClick={() => update({ profilePublic: !prefs.profilePublic })}
                        aria-pressed={prefs.profilePublic}
                    >
                        <span className="pref-toggle-thumb" />
                    </button>
                </div>
            </div>

            <div
                className={`pref-autosave${saveStatus === "saved" ? " pref-autosave-saved" : ""}${saveStatus === "error" ? " pref-autosave-error" : ""}`}
                aria-live="polite"
            >
                {saveStatus === "saving" && (<><span className="pref-autosave-spinner" />Saving…</>)}
                {saveStatus === "saved" && (<><Check size={14} />Saved</>)}
                {saveStatus === "error" && (
                    <>
                        <AlertTriangle size={14} />
                        Couldn't save your changes.
                        <button type="button" className="pref-autosave-retry" onClick={retry}>Retry</button>
                    </>
                )}
            </div>
        </>
    );
}

export default function Settings() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("Account");
    const [photo, setPhoto] = useState(() => (localStorage.getItem(ftUserPhoto) ?? "").replaceAll('"', ''));
    const [uploading, setUploading] = useState(false);
    const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
    const [userInfo, setUserInfo] = useState({ firstName: "", lastName: "", email: "", createdDate: "" });
    const [loading, setLoading] = useState(true);
    const fileInputRef = useRef(null);
    const photoMenuRef = useRef(null);

    useEffect(() => {
        request("GET", "/users/userinfo")
            .then(res => {
                setUserInfo(res);
                // if (res.imageUrl) setPhoto(res.imageUrl);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const handler = (e) => {
            if (photoMenuRef.current && !photoMenuRef.current.contains(e.target)) setPhotoMenuOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handlePhotoChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPhoto(URL.createObjectURL(file));
        setUploading(true);
        try {
            const { presignedUrl } = await getPhotoPresigned(file.type);
            await fetch(presignedUrl.url, { method: "PUT", body: file });
            await saveUserPhotoKey(presignedUrl.key);
            const updated = await request("GET", "/users/userinfo");
            if (updated?.imageUrl) {
                setPhoto(updated.imageUrl.replaceAll('"', ''));
                localStorage.setItem(ftUserPhoto, updated.imageUrl.replaceAll('"', ''));
                window.location.reload();
            }
        } catch {
            // preview stays, upload silently fails
        } finally {
            setUploading(false);
        }
    };

    const handleDeletePhoto = async () => {
        setPhotoMenuOpen(false);
        try {
            await deleteUserPhoto();
            localStorage.removeItem(ftUserPhoto);
            setPhoto("");
            window.location.reload();
        } catch {}
    };

    const handleDeleteAccount = async () => {
        if (!window.confirm("Are you sure you want to delete your account? This cannot be undone.")) return;
        try {
            await deleteAccountCall(true);
            localStorage.removeItem(ftAccessToken);
            localStorage.removeItem(ftRefreshToken);
            localStorage.removeItem(ftUserPhoto);
            localStorage.removeItem(ftUnreadCount);
            navigate("/");
        } catch {
            alert("Failed to delete account. Please try again.");
        }
    };

    return (
        <div className="home-layout">
            <Sidebar activePage="Settings" />

            <main className="home-main settings-main">
                <Header />

                <div className="settings-split">
                    <div className="settings-scroll">
                    <div className="settings-page">
                        {/* Profile Hero */}
                        <div className="settings-hero" style={{ backgroundImage: `url(${CoralReef})` }}>
                            <div className="settings-hero-overlay" />
                            <div className="settings-hero-content">
                                <div className="settings-hero-avatar-wrap" ref={photoMenuRef} style={{ position: "relative" }}>
                                    <div
                                        onClick={() => {
                                            if (uploading) return;
                                            if (photo) setPhotoMenuOpen(o => !o);
                                            else fileInputRef.current.click();
                                        }}
                                        style={{ cursor: uploading ? "default" : "pointer" }}
                                    >
                                        <div className="settings-hero-avatar">
                                            {photo
                                                ? <img src={photo} alt="Profile" className="settings-hero-avatar-img" />
                                                : `${userInfo.firstName?.[0] ?? ""}${userInfo.lastName?.[0] ?? ""}`
                                            }
                                        </div>
                                        <div
                                            className="settings-hero-avatar-overlay"
                                            style={uploading ? { opacity: 1 } : undefined}
                                        >
                                            {uploading
                                                ? <div className="settings-avatar-spinner" />
                                                : <Camera size={14} color="white" />
                                            }
                                        </div>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handlePhotoChange}
                                    />
                                    {photoMenuOpen && (
                                        <div className="photo-menu-dropdown">
                                            <button className="photo-menu-item" onClick={() => { setPhotoMenuOpen(false); fileInputRef.current.click(); }}>
                                                <Camera size={13} />
                                                Upload Photo
                                            </button>
                                            <button className="photo-menu-item photo-menu-item-danger" onClick={handleDeletePhoto}>
                                                <Trash2 size={13} />
                                                Delete Photo
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="settings-hero-name">{userInfo.firstName} {userInfo.lastName}</p>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="settings-tabs">
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    className={`settings-tab${activeTab === tab ? " settings-tab-active" : ""}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {activeTab === "Account" && (
                            <>
                                {/* Profile */}
                                <div className="settings-card">
                                    <p className="settings-card-title">Profile</p>
                                    <div className="settings-profile-fields">
                                        {loading ? (
                                            Array.from({ length: 4 }).map((_, i) => (
                                                <div key={i} className="settings-field-row">
                                                    <div className="skeleton" style={{ height: 11, width: 72 }} />
                                                    <div className="skeleton" style={{ height: 11, width: 160 }} />
                                                </div>
                                            ))
                                        ) : [
                                            { label: "First Name", value: userInfo.firstName },
                                            { label: "Last Name", value: userInfo.lastName },
                                            { label: "Email", value: userInfo.email },
                                            { label: "Date Created", value: userInfo.dateCreated ? new Date(userInfo.dateCreated).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—" },
                                        ].map((field) => (
                                            <div key={field.label} className="settings-field-row">
                                                <span className="settings-field-label">{field.label}</span>
                                                <span className="settings-field-value">{field.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Account Actions */}
                                <div className="settings-card">
                                    <p className="settings-card-title">Account Actions</p>
                                    <div className="settings-actions-list">
                                        {accountActions.map((action) => {
                                            const Icon = action.icon;
                                            const handleClick = action.label === "Delete Account"
                                                ? handleDeleteAccount
                                                : action.label === "Log Out"
                                                ? () => logoutCall(true)
                                                : () => action.path && navigate(action.path);
                                            return (
                                                <button
                                                    key={action.label}
                                                    className={`settings-action-row${action.danger ? " settings-action-danger" : ""}`}
                                                    onClick={handleClick}
                                                >
                                                    <Icon size={15} />
                                                    {action.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}

                        {activeTab === "Preferences" && (
                            <>
                                <Appearance />
                                <Preferences />
                            </>
                        )}

                        {activeTab === "Privacy" && (
                            <div className="settings-card">
                                <p className="settings-card-title">Legal</p>
                                <div className="settings-actions-list">
                                    <a className="settings-action-row" href="/terms" target="_blank" rel="noopener noreferrer">
                                        Terms of Service
                                    </a>
                                    <a className="settings-action-row" href="/privacy" target="_blank" rel="noopener noreferrer">
                                        Privacy Policy
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                    </div>

                    {/* Right image panel */}
                    <div className="settings-image-panel" style={{ backgroundImage: `url(${Frankophage})` }}>
                        <div className="settings-image-panel-overlay" />
                        <div className="settings-image-panel-caption">
                            <p className="featured-label">Science spotlight</p>
                            <p className="featured-title">Bacteriophage</p>
                            <p className="featured-authors">Illustration of a T4-like bacteriophage structure</p>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
